import React, { createContext, useContext, useEffect, useState } from "react";
import { ECLLoader } from "../../eclComponents/";
import { useNavigate } from "react-router-dom";
import sourceOneRestRequest from "../requests/SourceOneRestRequest";
import { ToastContext } from "./ToastContext";
import { AuthContext } from "./AuthContext";
import { translate } from "../Translate";
import { builtInApps } from "../../eclComponents/Widgets/ECLBuiltInApps/ECLBuiltInApps";
/**
 * App Context holds all meta information related to apps available on the server, as well as functions to install, launch and manage applications on the client.
 * @type {Object}
 * @property {array} all - full list of apps available on the server, with all relevant metadata
 * @property {array} installed - array of UUIDs that represent installed applications on the local client
 * @property {string} launchApp - UUID of the set launch app
 * @property {function} installApp - (appUUID) - install an app on a local client
 * @property {function} uninstallApp - (appUUID) - uninstall app on a local client
 * @property {function} setAsLaunchApp - (appUUID) - set app to launch automatically on local client
 * @property {function} removeLaunchApp - () - clears the set launch app on local client
 * @property {function} navigate - (appUUID) - navigates to an application
 */
const AppContext = createContext();
const AppContextProvider = (props) => {
   //Want to use navigate as an exportable function, so need to bind react router's navigate function with a wordier variable
   const reactRouterNavigate = useNavigate();
   const [all, setAll] = useState(null);
   const [installed, setInstalled] = useState(() => {
      const currentApps = localStorage.getItem("installedApps");
      if (!currentApps) {
         return [];
      }
      return JSON.parse(currentApps);
   });
   const [launchApp, setLaunchApp] = useState(() => {
      const launchApp = localStorage.getItem("launchApp");
      if (!launchApp) {
         return null;
      }
      return launchApp;
   });
   const [error, setError] = useState(null);

   const addToast = useContext(ToastContext);
   const auth = useContext(AuthContext);

   useEffect(() => {
      sourceOneRestRequest({
         query_type: "app_list",
      })
         .then((res) => {
            const updatedApps = [...builtInApps, ...res.apps];
            setAll(updatedApps);
         })
         .catch((error) => {
            console.error(error);
            setError("Application request failed");
            addToast(translate("Application request failed"));
            auth.logout();
         });
      if (
         launchApp &&
         (window.location.pathname === "/sourceone/" ||
            window.location.pathname === "/sourceone")
      ) {
         reactRouterNavigate("/sourceone/applications/app/" + launchApp);
      }
   }, []);

   /**
    * Installs Application
    * @param {string} appId
    */
   function installApp(appId) {
      if (!installed) {
         localStorage.setItem("installedApps", JSON.stringify([appId]));
         setInstalled([appId]);
      } else {
         let freshAppsObject = installed.filter((a) => !(a === appId));
         freshAppsObject.unshift(appId);
         localStorage.setItem("installedApps", JSON.stringify(freshAppsObject));
         setInstalled(freshAppsObject);
      }
      document.body.focus();
   }
   /**
    * Uninstalls App
    * @param {string} appId
    */
   function uninstallApp(appId) {
      const currentApps = localStorage.getItem("installedApps");
      const freshAppsObject = JSON.parse(currentApps).filter(
         (a) => !(a === appId),
      );
      localStorage.setItem("installedApps", JSON.stringify(freshAppsObject));
      setInstalled(freshAppsObject);
      document.body.focus();
   }
   /**
    * Sets Launch App
    * @param {string} appId
    */
   function setAsLaunchApp(appId) {
      localStorage.setItem("launchApp", appId);
      setLaunchApp(appId);
      installApp(appId);
      document.body.focus();
   }
   /**
    * Removes launch app
    */
   function removeLaunchApp() {
      localStorage.removeItem("launchApp");
      setLaunchApp(null);
      document.body.focus();
   }
   /**
    * Navigates to an app
    * @param {string} appId
    */
   function navigate(appId) {
      reactRouterNavigate(`/sourceone/applications/app/${appId}`);
      let filteredInstalledApps = installed.filter((a) => !(appId === a));
      filteredInstalledApps.unshift(appId);
      localStorage.setItem(
         "installedApps",
         JSON.stringify(filteredInstalledApps),
      );
      setInstalled(filteredInstalledApps);
      document.body.focus();
   }

   if (!all || error) {
      return <ECLLoader message={error} />;
   }

   return (
      <AppContext.Provider
         value={{
            all,
            installed,
            launchApp,
            installApp,
            setInstalled,
            uninstallApp,
            setAsLaunchApp,
            removeLaunchApp,
            navigate,
         }}
      >
         {props.children}
      </AppContext.Provider>
   );
};

export { AppContextProvider, AppContext };
